import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { InputText } from "primereact/inputtext";
import PageTitle from "../common/PageTitle";
import TabHeader from "../common/TabHeader";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import HasRequiredAuthority from "../dependencies/HasRequiredAuthority";
import { AUTHORITIES } from "../../constants";
import { getWriteableDate } from "../../utils";
import { TEXT_NORMAL } from "../../style";

function toPresentMap(students = []) {
    return students.reduce((acc, student) => {
        acc[student.user_id] = student.status === "PRESENT";
        return acc;
    }, {});
}

export default function BatchAttendance() {
    const { batchId } = useParams();
    const { requestAPI, showToast } = useAppContext();

    const [date, setDate] = useState(new Date());
    const [students, setStudents] = useState([]);
    const [presentByUserId, setPresentByUserId] = useState({});
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState();
    const [saving, setSaving] = useState();
    const [error, setError] = useState();

    const attendanceDate = useMemo(() => getWriteableDate({ date, removeTime: true }), [date]);

    useEffect(() => {
        if (!attendanceDate || attendanceDate === "--") {
            return;
        }

        requestAPI({
            requestPath: `batches/${batchId}/attendance`,
            requestMethod: "GET",
            requestGetQuery: { date: attendanceDate },
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    setStudents(data.students || []);
                    setPresentByUserId(toPresentMap(data.students || []));
                } else {
                    setError("Couldn't load Attendance");
                }
            },
        });
    }, [attendanceDate, batchId, requestAPI]);

    const filteredStudents = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) {
            return students;
        }
        return students.filter((student) => (student.full_name || "").toLowerCase().includes(query));
    }, [search, students]);

    const presentCount = useMemo(() => Object.values(presentByUserId).filter(Boolean).length, [presentByUserId]);
    const absentCount = students.length - presentCount;

    const togglePresent = useCallback((userId, checked) => {
        setPresentByUserId((prev) => ({ ...prev, [userId]: checked }));
    }, []);

    const markAllPresent = useCallback(() => {
        setPresentByUserId(
            students.reduce((acc, student) => {
                acc[student.user_id] = true;
                return acc;
            }, {}),
        );
    }, [students]);

    const saveAttendance = useCallback(() => {
        requestAPI({
            requestPath: `batches/${batchId}/attendance`,
            requestMethod: "POST",
            requestPostBody: {
                attendance_date: attendanceDate,
                records: students.map((student) => ({
                    user_id: student.user_id,
                    status: presentByUserId[student.user_id] ? "PRESENT" : "ABSENT",
                })),
            },
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save Attendance !", life: 2000 }),
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) {
                    showToast({ severity: "success", summary: "Saved", detail: "Attendance Saved", life: 1000 });
                    setStudents(data.students || []);
                    setPresentByUserId(toPresentMap(data.students || []));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Save Attendance !", life: 2000 });
                }
            },
        });
    }, [attendanceDate, batchId, presentByUserId, requestAPI, showToast, students]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Attendance" />
            <TabHeader className="px-3 pt-3" title="Batch Attendance" highlights={[`Batch #${batchId}`, attendanceDate]} />
            <Divider />

            <div className="px-3 flex align-items-center gap-2 flex-wrap">
                <Calendar
                    value={date}
                    onChange={(e) => setDate(e.value || new Date())}
                    dateFormat="dd/mm/yy"
                    disabled={loading || saving}
                    className="flex-1"
                    inputClassName="w-full"
                />
                <HasRequiredAuthority requiredAuthority={AUTHORITIES.MANAGE_BATCH_ATTENDANCE}>
                    <Button label="Save" icon="pi pi-save" severity="warning" loading={saving} disabled={loading || !students.length} onClick={saveAttendance} />
                </HasRequiredAuthority>
            </div>

            <div className={`px-3 mt-3 flex gap-3 align-items-center flex-wrap ${TEXT_NORMAL}`}>
                <span>Total {students.length}</span>
                <span className="text-red-500">Absent {absentCount}</span>
                <span className="text-green-500">Present {presentCount}</span>
                <Button
                    label="Mark all present"
                    size="small"
                    severity="success"
                    text
                    disabled={loading || saving || !students.length}
                    onClick={markAllPresent}
                />
            </div>

            <div className="px-3 mt-3">
                <InputText
                    value={search}
                    placeholder="Search by name"
                    className="w-full"
                    onChange={(e) => setSearch(e.target.value)}
                    disabled={loading || saving}
                    pt={{ root: { className: TEXT_NORMAL } }}
                />
            </div>

            <div className="flex-1 overflow-y-scroll p-3 flex flex-column gap-2">
                {loading ? (
                    <Loading />
                ) : error ? (
                    <NoContent error={error} />
                ) : filteredStudents.length ? (
                    filteredStudents.map((student) => (
                        <div key={student.user_id} className="flex align-items-center gap-3 border-1 border-gray-300 border-round py-2 px-3">
                            <div className={`flex-1 ${TEXT_NORMAL} font-semibold`}>
                                {student.roll_no != null ? `${student.roll_no}. ` : null}
                                {student.full_name || "—"}
                            </div>
                            <Checkbox
                                inputId={`attendance-${student.user_id}`}
                                checked={!!presentByUserId[student.user_id]}
                                onChange={(e) => togglePresent(student.user_id, e.checked)}
                                disabled={saving}
                            />
                        </div>
                    ))
                ) : (
                    <NoContent error={students.length ? "No matching students" : "No Students In Batch"} />
                )}
            </div>
        </div>
    );
}
